import { useMemo, useState } from 'react';
import {
  Alert, AppBar, Avatar, Badge, Box, CircularProgress, Divider, IconButton, Menu, MenuItem,
  Snackbar, Toolbar, Tooltip, Typography, useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { SHOW_HEADER_NOTIFICATIONS } from '../../../constants/headerNotifications';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import CheckIcon from '@mui/icons-material/Check';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useAuthStore from '../../stores/useAuthStore';
import useBreadcrumbTitleStore from '../../stores/useBreadcrumbTitleStore';
import breadcrumbsFor from '../../config/breadcrumbs';
import { readableTextOn } from '../../theme/brandTokens';
import WalletBalanceChip from './WalletBalanceChip';
import LogoutConfirmDialog from '../../../components/common/LogoutConfirmDialog';
import { palette, typography, radius, size, borderWidth } from 'theme/tokens';

/**
 * The partner console's top bar — MUI, built to the CRM's `Header.jsx`.
 *
 * Same chrome as the CRM: a sticky 64px white AppBar with a hairline bottom
 * border, the notification bell on the right, a vertical rule, then the
 * identity block. Logout is its own icon button beside it, matching the CRM's
 * exactly. The one deliberate difference is that the identity block is ALSO a
 * menu trigger — clicking it opens a small menu with the email and partner
 * code (neither has anywhere else to live in this header) and a Profile
 * entry; the CRM's own avatar+name is static and carries no menu.
 */
export default function PartnerHeader({ isMobile = false, onMenuClick }) {
  const theme = useTheme();
  const { user, logout } = useAuthStore();
  const [confirmLogout, setConfirmLogout] = useState(false);
  const location = useLocation();
  const { crumbs, title: derivedTitle } = breadcrumbsFor(location.pathname);
  const dynamicTitle = useBreadcrumbTitleStore((s) => (s.path === location.pathname ? s.label : ''));
  // A record-detail page (CustomerDetailPage, …) publishes its own loaded
  // record's name once it resolves; until then, or for any route that never
  // does, the pathname-derived label — "Customers", or the raw id for an
  // unmapped segment — is what's shown.
  const title = dynamicTitle || derivedTitle;
  const brand = theme.palette.primary.main;
  const [profileAnchor, setProfileAnchor] = useState(null);
  const navigate = useNavigate();

  /**
   * WHICH BRANCH THIS SESSION IS WORKING AT.
   *
   * A partner trading from more than one branch prices, invoices and reports per
   * branch, so "which one is this booking at" is a real question, and the header
   * is where it is answered — the same question the CRM's acting-partner bar
   * asks an admin booking on their behalf.
   *
   * The DEFAULT is the first branch the session lists. That is not an arbitrary
   * pick: `PartnerAuthRepository.contextFor` orders them `is_default DESC,
   * createdAt ASC`, the same order the server itself falls back through when a
   * request names no branch — so what is shown is what is already happening,
   * not a claim about it.
   *
   * Choosing one exchanges the ACCESS TOKEN for one carrying that branch
   * (`useAuthStore.switchBranch`): for a partner session the token is the only
   * carrier the engines trust, since `x-acting-branch-code` is honoured for CRM
   * sessions only.
   */
  const branches = useAuthStore((s) => s.branches);
  const actingBranchCode = useAuthStore((s) => s.actingBranchCode);
  const switchBranch = useAuthStore((s) => s.switchBranch);
  const [switching, setSwitching] = useState(false);
  /**
   * What the switch DID, said out loud.
   *
   * Both outcomes go here, in the partner portal's own idiom (Snackbar +
   * Alert, as BrandingPage and the commercials screens use). A failure used to
   * live in the tooltip, which meant the operator had to hover the control to
   * learn why nothing happened; a success said nothing at all, so a switch that
   * worked and a switch that silently failed looked identical.
   */
  const [branchNotice, setBranchNotice] = useState(null);
  const branchOptions = useMemo(() => {
    const list = Array.isArray(branches) ? branches : [];
    // A suspended or closed branch is not a place a booking can be made. If that
    // leaves nothing, show them all rather than an empty control — a partner
    // whose branches are all inactive is a data problem to see, not one to be
    // silently locked out by.
    const active = list.filter((b) => String(b?.status || '').toLowerCase() === 'active');
    return active.length ? active : list;
  }, [branches]);

  const selectedBranch = actingBranchCode ?? branchOptions[0]?.id ?? '';

  /**
   * The switch itself, by branch id — NOT by change event.
   *
   * Driven by the profile menu's branch rows at every screen size — the bar
   * itself carries no branch selector.
   */
  const applyBranch = async (value) => {
    if (value === selectedBranch) return;
    setSwitching(true);
    setBranchNotice(null);
    const result = await switchBranch(value);
    setSwitching(false);
    if (!result?.ok) {
      setBranchNotice({ severity: 'error', message: result?.message || 'Could not change the branch.' });
      return;
    }
    const name = branchOptions.find((b) => b.id === value)?.name;
    setBranchNotice({
      severity: 'success',
      message: name ? `Now working at ${name}` : 'Branch changed',
    });
  };

  const closeProfile = () => setProfileAnchor(null);

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: palette.white,
        borderBottom: `${borderWidth.thin} solid ${palette.neutral[70]}`,
        zIndex: theme.zIndex.appBar,
      }}
    >
      <Toolbar sx={{ minHeight: size[64], px: isMobile ? 1.5 : 3, gap: 1 }}>
        {isMobile && (
          <IconButton edge="start" onClick={onMenuClick} sx={{ mr: 0.5, color: theme.palette.text.primary }}>
            <MenuIcon />
          </IconButton>
        )}

        {/*
          THE BAR CARRIES THE TRAIL AND A SHORT NAV-DERIVED TITLE, styled
          exactly like the CRM's own `Header.jsx` (primary-coloured links,
          `text.disabled` separators, underline on hover). Shown at every
          screen size: the wallet and branch moved into the profile menu, so
          the bar now has room for it on phones and tablets too.
        */}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          {crumbs.length > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'nowrap', overflow: 'hidden' }}>
              {crumbs.slice(0, -1).map((c) => (
                <Box key={c.label + (c.to || '')} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                  <Typography
                    component={c.to ? Link : 'span'}
                    to={c.to || undefined}
                    variant="body2"
                    color={c.to ? 'primary' : 'text.secondary'}
                    sx={{ textDecoration: 'none', whiteSpace: 'nowrap', '&:hover': c.to ? { textDecoration: 'underline' } : undefined }}
                  >
                    {c.label}
                  </Typography>
                  <Typography variant="body2" color="text.disabled">/</Typography>
                </Box>
              ))}
            </Box>
          )}
          <Typography
            variant="h4"
            sx={{
              color: theme.palette.text.primary,
              mt: crumbs.length > 1 ? 0.25 : 0,
              lineHeight: typography.lineHeight.tight,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {title}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {SHOW_HEADER_NOTIFICATIONS && (
            <Tooltip title="Notifications">
              <IconButton sx={{ color: palette.neutral[470], '&:hover': { backgroundColor: palette.neutral[40] } }}>
                {/*
                  THE COUNT IS BRANDED, NOT RED — MUI's `color="error"` would make
                  it read as an error indicator rather than "you have three of
                  these", and red is the one colour the partner never chose.

                  The digits are derived rather than left white: the branding
                  screen permits a light primary (Zenvoya's own secondary, #A1F919,
                  is 1.31:1 against white) and a count nobody can read is worse
                  than a red one. Same helper, same reason, as the sidebar tile.
                */}
                <Badge
                  // SMB-4215: was a hardcoded placeholder count. There is no notifications
                  // API yet, so 0 — which MUI hides — until a real unread count is wired in.
                  badgeContent={0}
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: brand,
                      color: readableTextOn(brand),
                      fontSize: typography.fontSize[10],
                      minWidth: size[18],
                      height: size[18],
                    },
                  }}
                >
                  <NotificationsNoneIcon sx={{ fontSize: typography.fontSize[22] }} />
                </Badge>
              </IconButton>
            </Tooltip>
          )}

          <Divider orientation="vertical" flexItem sx={{ mx: 1, my: 1.5 }} />

          {/* The identity block IS the menu trigger — the one deliberate
              difference from the CRM's header. */}
          <Box
            onClick={(e) => setProfileAnchor(e.currentTarget)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 0.5,
              py: 0.5,
              borderRadius: `${radius.lg}px`,
              cursor: 'pointer',
              '&:hover': { backgroundColor: palette.neutral[40] },
            }}
          >
            <Avatar sx={{ width: size[34], height: size[34], backgroundColor: brand, fontSize: typography.fontSize[14], fontWeight: typography.fontWeight.bold }}>
              {user?.name?.charAt(0) || <PersonOutlineIcon sx={{ fontSize: typography.fontSize[20] }} />}
            </Avatar>
            {/* A name takes real estate a phone-width header does not have. */}
            {!isMobile && (
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: typography.fontWeight.semiBold, color: theme.palette.text.primary, lineHeight: typography.lineHeight.tight, fontSize: typography.fontSize[12] }}>
                  {user?.name || 'Admin'}
                </Typography>
              </Box>
            )}
          </Box>

          <Menu
            anchorEl={profileAnchor}
            open={Boolean(profileAnchor)}
            onClose={closeProfile}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: {
                sx: {
                  minWidth: size[260],
                  maxWidth: size[300],
                  mt: 1,
                  borderRadius: `${radius.xl}px`,
                  border: `${borderWidth.thin} solid ${palette.neutral[70]}`,
                },
              },
            }}
            MenuListProps={{ sx: { py: 1 } }}
          >
            <WalletBalanceChip inMenu />

            {/*
              THE BRANCH lives here at every screen size — the bar no longer
              carries a selector. One branch renders as a LABEL, not a row: a
              tappable row that cannot change anything is a lie about what is
              available. More than one renders as rows with a tick on the
              current branch.
            */}
            {branchOptions.length > 0 && (
              <>
                <Divider />
                <Box sx={{ px: 2, pt: 1.5, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography
                    sx={{
                      fontSize: typography.fontSize[10],
                      fontWeight: typography.fontWeight.semiBold,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      color: palette.tw.gray[400],
                    }}
                  >
                    Branch
                  </Typography>
                  {switching && <CircularProgress size={size[12]} sx={{ color: palette.neutral[470] }} />}
                </Box>
                {branchOptions.map((b) => {
                  const current = b.id === selectedBranch;
                  const single = branchOptions.length === 1;
                  const rowSx = {
                    mx: 1,
                    mb: 0.5,
                    px: 1,
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    borderRadius: `${radius.md}px`,
                    backgroundColor: current ? `${brand}10` : 'transparent',
                    '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: `${brand}14` },
                  };
                  const content = (
                    <>
                      <Box
                        sx={{
                          width: size[32],
                          height: size[32],
                          flexShrink: 0,
                          borderRadius: `${radius.md}px`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: current ? `${brand}1A` : palette.neutral[40],
                        }}
                      >
                        <StorefrontOutlinedIcon sx={{ fontSize: typography.fontSize[18], color: current ? brand : palette.tw.gray[400] }} />
                      </Box>
                      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                        <Typography noWrap sx={{ fontSize: typography.fontSize[12], fontWeight: current ? typography.fontWeight.semiBold : typography.fontWeight.medium, color: theme.palette.text.primary }}>
                          {b.name || `Branch ${b.id}`}
                        </Typography>
                        {(b.city || b.currency) && (
                          <Typography noWrap sx={{ fontSize: typography.fontSize[12], color: palette.tw.gray[400] }}>
                            {[b.city, b.currency].filter(Boolean).join(' · ')}
                          </Typography>
                        )}
                      </Box>
                      {current && !single && <CheckIcon sx={{ fontSize: typography.fontSize[18], color: brand, flexShrink: 0 }} />}
                    </>
                  );
                  return single ? (
                    <Box key={b.id} sx={rowSx}>{content}</Box>
                  ) : (
                    <MenuItem
                      key={b.id}
                      selected={current}
                      disabled={switching}
                      onClick={() => {
                        closeProfile();
                        applyBranch(b.id);
                      }}
                      sx={rowSx}
                    >
                      {content}
                    </MenuItem>
                  );
                })}
              </>
            )}

            <Divider sx={{ my: 1 }} />

            <MenuItem
              onClick={() => {
                closeProfile();
                navigate('/partnerAdmin/profile');
              }}
              sx={{ mx: 1, px: 1, py: 1, gap: 1.5, borderRadius: `${radius.md}px`, fontSize: typography.fontSize[12], color: palette.neutral[670], '&:hover, &.Mui-focusVisible': { backgroundColor: palette.neutral[40] } }}
            >
              <PersonOutlineIcon fontSize="small" sx={{ color: palette.tw.gray[500] }} /> Profile
            </MenuItem>
          </Menu>

          {/* Logout is its own icon button, matching the CRM's — not a menu
              item behind the profile click. */}
          <Tooltip title="Logout">
            <IconButton
              onClick={() => setConfirmLogout(true)}
              sx={{ color: palette.neutral[330], ml: 0.5, '&:hover': { backgroundColor: palette.custom.red[30], color: palette.mui.red[700] } }}
            >
              <LogoutIcon sx={{ fontSize: typography.fontSize[20] }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>

      <LogoutConfirmDialog
        open={confirmLogout}
        onCancel={() => setConfirmLogout(false)}
        onConfirm={() => {
          setConfirmLogout(false);
          logout();
        }}
      />

      {/* Anchored bottom-centre like every other notice in this portal, so a
          switch reports itself in the place operators already look. */}
      <Snackbar
        open={!!branchNotice}
        autoHideDuration={4000}
        onClose={() => setBranchNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {branchNotice ? (
          <Alert
            severity={branchNotice.severity}
            onClose={() => setBranchNotice(null)}
            sx={{ width: '100%' }}
          >
            {branchNotice.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </AppBar>
  );
}
